import {
  Button,
  Page,
  Stack,
  Text,
  TextField,
} from "@towersystems/roam-common-native";
import ColorPicker, {
  Panel1,
  Swatches,
  Preview,
  HueSlider,
} from "reanimated-color-picker";
import { useFormContext } from "react-hook-form";
import { View } from "react-native-ui-lib";

import React from "react";
import { NativeStackScreenProps } from "@react-navigation/native-stack";

export type ColorPickerScreenProps = NativeStackScreenProps<
  {
    ColorPickerScreen: {
      fieldName: string;
    };
  },
  "ColorPickerScreen"
>;

function isValidHex(hex: string) {
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hex.trim());
}

export const ColorPickerScreen = ({
  navigation,
  route,
}: ColorPickerScreenProps) => {
  const { watch, setValue } = useFormContext<any>();
  const watchedColorTheme = watch(route.params.fieldName ?? "");

  const [onChangeValue, setOnChangeValue] = React.useState(
    stripAlpha(watchedColorTheme) || "#FFFFFF"
  );
  const [hexInput, setHexInput] = React.useState(onChangeValue);

  // Sync
  React.useEffect(() => {
    setHexInput(stripAlpha(onChangeValue));
  }, [onChangeValue]);

  const handleHexInputChange = (input: string) => {
    if (!input.startsWith("#")) input = "#" + input.replace(/#/g, "");
    setHexInput(stripAlpha(input));
    if (isValidHex(input)) {
      setOnChangeValue(input);
    }
  };

  const footerMarkup = (
    <View row>
      <View flex>
        <Button
          label={"Use this color"}
          isAction
          onPress={() => {
            setValue(route.params.fieldName ?? "", onChangeValue, {
              shouldDirty: true,
              shouldValidate: true,
            });
            navigation.pop();
          }}
          disabled={!isValidHex(onChangeValue)}
        />
      </View>
    </View>
  );

  return (
    <Page
      title="Select Color"
      secondaryTitle="Select a color to apply "
      footer={footerMarkup}
    >
      <ColorPicker
        style={{ width: "70%" }}
        value={onChangeValue}
        onComplete={() => null}
        onChange={(color) => {
          setOnChangeValue(color.hex);
          setHexInput(color.hex.toUpperCase());
        }}
      >
        <Stack vertical spacing={10}>
          <Panel1 />
          <HueSlider />
          <Swatches
            colors={[
              "#ffffff", // white
              "#262625", // dark grey
              "#4394e5", // blue
              "#DFB93C", // modifier
              "#94B5FB", // action
              "#203D4E", // navy
              "#c7b6de", // lilac
              "#739db9", // lightBlue
              "#2F6246", // lightGreen
              "#bda16d", // gold
              "#f49342", // orange
            ]}
          />
          <View
            row
            centerV
            marginB-10
            style={{
              width: "100%",
              justifyContent: "flex-start",
              alignItems: "center",
              gap: 16,
            }}
          >
            <View row centerV>
              <Text text70 marginR-4 style={{ fontWeight: "bold" }}>
                HEX
              </Text>
              <TextField
                value={hexInput}
                onChangeText={handleHexInputChange}
                placeholder="#FFFFFF"
                autoCapitalize="characters"
                maxLength={7}
                enableErrors
                validate={["required", (v: string) => isValidHex(v)]}
                validateOnBlur
                showCharCounter={false}
                floatingPlaceholder={false}
              />
            </View>
          </View>
        </Stack>
      </ColorPicker>
    </Page>
  );
};

function stripAlpha(hex: string) {
  if (/^#([A-Fa-f0-9]{8})$/.test(hex)) {
    return hex.slice(0, 7); // #RRGGBB from #RRGGBBAA
  }
  return hex;
}
